import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useConvexAuth, useQuery } from 'convex/react'
import { Check } from 'lucide-react'
import { api } from '../lib/api'
import { appUrl } from '../lib/config'
import { purchasePass, isBillingConfigured, type PassId } from '../lib/revenuecat'
import { canUseTestCheckout } from '../lib/testCheckout'
import { Spinner } from './ui/Spinner'
import { BrandLogo } from './ui/BrandLogo'

const FEATURES = [
  'Ship smarter with analytics',
  'Collect email subscribers',
  'Connect a custom domain',
  'Lightning-fast, SEO-friendly pages',
  'Search engines follow your links',
  '30 themes and 9 fonts',
]

type Plan = { id: PassId; name: string; price: string; old?: string; popular?: boolean }

const PLANS: Plan[] = [
  { id: 'year_pass', name: '1-Year Pass', price: '$25' },
  { id: 'lifetime', name: 'Lifetime Deal', price: '$45', old: '$75', popular: true },
]

const TEST_PLAN: Plan = { id: 'test_pass', name: 'Production checkout test', price: '$1' }
const TEST_CHECKOUT_USER_ID = import.meta.env.VITE_TEST_CHECKOUT_USER_ID

export function Pricing() {
  const { isAuthenticated } = useConvexAuth()
  const me = useQuery(api.users.me, isAuthenticated ? {} : 'skip')
  const navigate = useNavigate()
  const [busy, setBusy] = useState<PassId | null>(null)
  const [error, setError] = useState<string | null>(null)
  const plans = canUseTestCheckout(me?._id, TEST_CHECKOUT_USER_ID) ? [...PLANS, TEST_PLAN] : PLANS

  async function deploy(id: PassId) {
    setError(null)
    if (!isAuthenticated) {
      window.location.href = appUrl('/login?next=/pricing')
      return
    }
    if (!me?._id) return
    if (!isBillingConfigured()) {
      setError('Billing is not configured yet. Set VITE_REVENUECAT_PUBLIC_KEY.')
      return
    }
    setBusy(id)
    try {
      await purchasePass(me._id, id)
      navigate('/admin')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Checkout failed. Please try again.')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100">
      <header className="mx-auto flex max-w-5xl items-center justify-between px-6 py-5">
        <Link to="/" className="text-white no-underline">
          <BrandLogo />
        </Link>
        <a href={appUrl('/admin')} className="text-sm text-zinc-300 hover:text-white">
          My page
        </a>
      </header>

      <main className="mx-auto max-w-5xl px-6 py-10 text-center">
        <h1 className="text-4xl font-extrabold leading-tight sm:text-5xl">
          Deploy your page, get more customers
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-zinc-400">
          Edit and preview for free. Pay once to take your page live — no subscription.
        </p>

        <div className="mx-auto mt-12 grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2">
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={`relative rounded-2xl border p-8 text-left ${
                plan.popular ? 'border-fuchsia-500 bg-zinc-900' : 'border-zinc-800 bg-zinc-900/60'
              }`}
            >
              {plan.popular && (
                <span className="absolute -top-3 right-6 rounded-full bg-fuchsia-500 px-3 py-1 text-xs font-bold uppercase">
                  Popular
                </span>
              )}
              <h2 className="text-lg font-bold">{plan.name}</h2>
              <div className="mt-4 flex items-end gap-2">
                {plan.old && <span className="text-xl text-zinc-600 line-through">{plan.old}</span>}
                <span className="text-5xl font-extrabold">{plan.price}</span>
                <span className="mb-1 text-sm text-zinc-500">USD</span>
              </div>
              <p className="mt-1 text-sm text-zinc-500">One-time payment. No subscription.</p>
              <button
                onClick={() => deploy(plan.id)}
                disabled={busy !== null}
                className={`mt-6 flex w-full items-center justify-center rounded-xl py-3 text-sm font-bold transition disabled:opacity-50 ${
                  plan.popular
                    ? 'bg-fuchsia-500 text-white hover:bg-fuchsia-400'
                    : 'bg-zinc-800 text-white hover:bg-zinc-700'
                }`}
              >
                {busy === plan.id ? <Spinner className="h-4 w-4" /> : 'Deploy now'}
              </button>
            </div>
          ))}
        </div>

        {error && <p className="mt-6 text-sm text-rose-400">{error}</p>}

        <ul className="mx-auto mt-12 grid max-w-2xl grid-cols-1 gap-3 text-left sm:grid-cols-2">
          {FEATURES.map((f) => (
            <li key={f} className="flex items-center gap-2 text-sm text-zinc-300">
              <Check className="h-4 w-4 flex-shrink-0 text-emerald-400" /> {f}
            </li>
          ))}
        </ul>
      </main>
    </div>
  )
}
